import React, { useRef, useEffect, useState } from 'react';
import BlobBot, { BotState } from './BlobBot';
import { gsap, useReducedMotion } from '../../animations';
import { useAgentAccent } from '../../lib/useAgentAccent';

interface AgentLauncherProps {
    state?: BotState;
    /** Overrides the page-driven accent. Omit to let the page decide. */
    color?: string;
    size?: number;
    open?: boolean;
    onToggle: () => void;
    /** Text in the pill that slides out on hover */
    hint?: string;
    label?: string;
}

/**
 * AgentLauncher — a small resident character in the corner of the page.
 *
 * It arrives after the page settles, notices the pointer, and opens the session
 * when invited. Presence as interface: the avatar is the control and the status
 * indicator at once, so there is no separate badge, spinner, or "1 unread" dot.
 *
 * Knows nothing about chat. Give it a state and a toggle and it will launch
 * anything.
 */
const AgentLauncher: React.FC<AgentLauncherProps> = ({
    state = 'idle',
    color,
    size = 58,
    open = false,
    onToggle,
    hint = 'ask me anything',
    label = 'Open the agent',
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [hovered, setHovered] = useState(false);
    const reduced = useReducedMotion();
    // The agent wears the page's colour unless told otherwise.
    const pageAccent = useAgentAccent();
    const accent = color ?? pageAccent;

    // Arrival: it drops in once the page has settled, rather than being present
    // at first paint. That beat is what makes it read as someone showing up.
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        if (reduced) {
            gsap.set(el, { y: 0, scale: 1, opacity: 1 });
            return;
        }
        const ctx = gsap.context(() => {
            gsap.fromTo(el,
                { y: 90, scale: 0.4, opacity: 0 },
                { y: 0, scale: 1, opacity: 1, duration: 1.0, delay: 0.7, ease: 'elastic.out(1, 0.62)' });
        }, el);
        return () => ctx.revert();
    }, [reduced]);

    // One nudge if it has been sitting unnoticed. Once — not a loop, because a
    // widget that keeps waving at you is a widget you learn to ignore.
    useEffect(() => {
        const el = rootRef.current;
        if (!el || reduced || open) return;
        const t = setTimeout(() => {
            gsap.to(el, {
                y: -12, duration: 0.34, ease: 'power2.out',
                yoyo: true, repeat: 3,
                onComplete: () => gsap.set(el, { y: 0 }),
            });
        }, 22000);
        return () => clearTimeout(t);
    }, [reduced, open]);

    return (
        <div
            ref={rootRef}
            className="agent-launcher"
            style={{
                position: 'fixed',
                right: 'clamp(16px, 3vw, 30px)',
                bottom: 'clamp(16px, 3vw, 30px)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                pointerEvents: 'none',
                willChange: 'transform',
            }}
        >
            {/* Hover pill. Slides in on transform only — never on width. */}
            <span
                aria-hidden
                style={{
                    fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    fontSize: '12px',
                    color: '#c9d1e3',
                    background: 'rgba(13,15,24,0.92)',
                    border: `1px solid ${accent}47`,
                    borderRadius: '999px',
                    padding: '7px 14px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 8px 24px -6px rgba(0,0,0,0.7)',
                    pointerEvents: 'none',
                    opacity: hovered && !open ? 1 : 0,
                    transform: hovered && !open ? 'translateX(0) scale(1)' : 'translateX(10px) scale(0.96)',
                    transition: reduced ? 'none' : 'opacity 0.22s ease, transform 0.28s cubic-bezier(0.16,1,0.3,1)',
                }}
            >
                {open ? 'close session' : hint}
            </span>

            <button
                onClick={onToggle}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                aria-label={label}
                aria-expanded={open}
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    lineHeight: 0,
                    borderRadius: '50%',
                    // Neutral directional elevation shadow avoids neon halo while lifting on hover
                    boxShadow: hovered
                        ? '0 14px 32px -8px rgba(0, 0, 0, 0.65)'
                        : '0 10px 24px -10px rgba(0, 0, 0, 0.5)',
                    transform: hovered && !reduced ? 'scale(1.07)' : 'scale(1)',
                    transition: reduced ? 'none' : 'transform 0.26s cubic-bezier(0.16,1,0.3,1), box-shadow 0.5s ease',
                }}
            >
                <BlobBot
                    variant="blob"
                    color={accent}
                    size={size}
                    state={state === 'idle' && (open || hovered) ? 'curious' : state}
                    trackPointer
                    presence
                />
            </button>
        </div>
    );
};

export default AgentLauncher;
